/**
 * "Download all assets" for a snapshot.
 *
 * A snapshot carries its assets as base64 inside the file the editor already
 * holds, so the archive is built from what is on screen — no request, no build,
 * nothing to keep in step with the export pipeline.
 *
 * Two places hold bytes and both go in:
 *   - `assets`: the asset map the Assets tab lists, keyed by asset key. A pair
 *     type (atlas, bitmap font) stores an object whose parts each have their own
 *     bytes, and each part becomes its own file.
 *   - `sounds`: the engine sound registry, which is a separate list of slots the
 *     asset map knows nothing about.
 *
 * A slot with no inline bytes — an empty engine sound, or a "@server:" reference
 * from the version editor — has nothing to write, and is reported as skipped so
 * the caller can say so rather than quietly shipping a short archive.
 */

import _ from "lodash";

import { buildZipBytes, dataUrlToBytes, extensionFromDataUrl } from "./zipArchive";

/** Engine sounds are audio too; their own folder keeps the two lists apart. */
const ENGINE_SOUND_FOLDER = "engine-sounds";

/** Strip what a file name may not carry, so an asset key can become one. */
function safeName(name) {
  return String(name || "asset").replace(/[/\\?%*:|"<>]/g, "-").trim() || "asset";
}

/** The data URLs inside one entry's `source`: one for a plain type, two for a pair. */
function sourcesFromEntry(source) {
  if (typeof source === "string") return [source];
  if (_.isPlainObject(source)) return Object.values(source).filter((v) => typeof v === "string");
  return [];
}

function addEntry(files, skipped, { folder, key, source }) {
  const urls = sourcesFromEntry(source).filter((url) => url.startsWith("data:"));
  if (urls.length === 0) {
    skipped.push(key);
    return;
  }
  for (const url of urls) {
    const bytes = dataUrlToBytes(url);
    if (!bytes || bytes.length === 0) {
      skipped.push(key);
      continue;
    }
    files.push({
      path: `${folder}/${safeName(key)}.${extensionFromDataUrl(url)}`,
      bytes,
    });
  }
}

/**
 * Every asset in a snapshot, as archive entries.
 *
 * @param {object} snapshot - the playEngine config the editor has open
 * @returns {{ files: Array<{path: string, bytes: Uint8Array}>, skipped: string[] }}
 */
export function collectSnapshotAssetFiles(snapshot) {
  const files = [];
  const skipped = [];

  const assets = _.get(snapshot, "assets");
  if (_.isPlainObject(assets)) {
    for (const [key, entry] of Object.entries(assets)) {
      addEntry(files, skipped, {
        folder: safeName(_.get(entry, "type") || "asset"),
        key,
        source: _.get(entry, "source"),
      });
    }
  }

  const sounds = _.get(snapshot, "sounds");
  if (_.isPlainObject(sounds)) {
    for (const [key, entry] of Object.entries(sounds)) {
      addEntry(files, skipped, {
        folder: ENGINE_SOUND_FOLDER,
        key,
        source: _.get(entry, "source"),
      });
    }
  }

  return { files, skipped };
}

/** "test_01_export_abc.json" → "test_01_export_abc-assets.zip" */
export function assetArchiveName(snapshotFileName) {
  const stem = String(snapshotFileName || "snapshot").replace(/\.json$/i, "");
  return `${safeName(stem)}-assets.zip`;
}

/**
 * Build the archive and hand it to the browser as a download.
 *
 * @returns {{ count: number, skipped: string[] }} what the archive ended up with
 */
export function downloadSnapshotAssets(snapshot, snapshotFileName) {
  const { files, skipped } = collectSnapshotAssetFiles(snapshot);
  if (files.length === 0) return { count: 0, skipped };

  const blob = new Blob([buildZipBytes(files)], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = assetArchiveName(snapshotFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox needs the object to outlive the click; a tick is enough for both.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return { count: files.length, skipped };
}
